import { crmLevelTypeAliasesForTicketType } from './crmCommunicationTypeAliases';
import { isUserInCrmLevel } from './crmCommunicationDeadlineAccess';
import { isTicketCompanyContractor } from './crmCommunicationContractorAccess';

function isCrmWorkflowFinalStatus(state) {
  const tt = String(state?.transition_type || '');
  return tt === 'final_auto' || tt === 'final_manually';
}

function isCrmApprovalInProgress(ticket, crmStates) {
  const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
  const status = (crmStates || []).find((s) => Number(s.id) === statusId);
  if (!status) return false;
  const phase = String(status.phase || '');
  const tt = String(status.transition_type || '');
  return phase === 'created'
    || (['approval', 'system_approval'].includes(phase) && ['non_final', 'level_final'].includes(tt));
}

/**
 * Môže meniť oddelenie/zamestnanca v Nasledujúcom kroku – parita ServiceTaskDetail::checkUserDepartment.
 */
export function canUserSetCrmNextStepAssignee(ticket, user, state, workflowLocked = false, approvalInProgress = false) {
  if (!ticket || !user) return false;
  if (workflowLocked || ticket.cancelled_at) return false;
  if (Number(ticket.created_by_user_id) === Number(user.id)) return true;
  if (isTicketCompanyContractor(ticket, user)) return true;
  if (user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin') return true;
  if (Array.isArray(user?.roles) && user.roles.some((r) => String(r?.name || r).toLowerCase() === 'admin')) return true;
  const type = String(ticket.type || '');
  if (type !== 'acquisition' && type !== 'quote_request') return false;

  const crmStates = Array.isArray(state?.crm_states) ? state.crm_states : [];
  const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
  const status = crmStates.find((s) => Number(s.id) === statusId);
  if (status && isCrmWorkflowFinalStatus(status)) return false;
  if (approvalInProgress) return false;

  const awaiting = Array.isArray(ticket?.awaiting_user) ? ticket.awaiting_user : [];
  if (awaiting.some((u) => Number(u?.id) === Number(user.id))) return true;

  const approvalRows = Array.isArray(ticket?.approval_requests)
    ? ticket.approval_requests
    : (Array.isArray(ticket?.approval) ? ticket.approval : []);
  if (approvalRows.some((row) => Number(row?.approver_id) === Number(user.id))) return true;
  if (approvalRows.some((row) => (
    Array.isArray(row?.possible_approvers)
    && row.possible_approvers.some((id) => Number(id) === Number(user.id))
  ))) return true;

  const aliases = crmLevelTypeAliasesForTicketType(type);
  const managementLevels = (state?.crm_levels || []).filter(
    (lvl) => String(lvl?.task || '') === 'management' && aliases.includes(String(lvl?.type || '')),
  );
  if (managementLevels.length > 0) {
    const managementLevelIds = managementLevels.map((lvl) => Number(lvl.id));
    const hasConfiguredManager = (state?.crm_level_user_settings || []).some(
      (setting) => managementLevelIds.includes(Number(setting?.crm_level_id)),
    );
    if (hasConfiguredManager) {
      return managementLevelIds.some((levelId) => isUserInCrmLevel(state, user, levelId));
    }
  }

  const approvalLevels = (state?.crm_levels || []).filter(
    (lvl) => String(lvl?.task || '') === 'approval' && aliases.includes(String(lvl?.type || '')),
  );
  return approvalLevels.some((lvl) => isUserInCrmLevel(state, user, lvl.id));
}

/**
 * Môže nastaviť kontrolóra – parita ServiceTaskDetail::checkInspector (inverzia disabled).
 * Kým nie je nastavený stav výsledku, môže meniť aj ktokoľvek z possible_inspectors.
 */
export function canUserAssignCrmInspector(ticket, user, state, row, workflowLocked = false, approvalInProgress = false) {
  if (!ticket || !user || !row) return false;
  if (workflowLocked || ticket.cancelled_at) return false;
  if (isCrmInspectorReassignLocked(row)) return false;

  const crmStates = Array.isArray(state?.crm_states) ? state.crm_states : [];
  const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
  const status = crmStates.find((s) => Number(s.id) === statusId);
  if (status) {
    if (isCrmWorkflowFinalStatus(status)) return false;
    if (['created', 'system_approval'].includes(String(status.phase || ''))) return false;
  }
  if (approvalInProgress) return false;

  const possibleInspectors = Array.isArray(row.possible_inspectors) ? row.possible_inspectors : [];
  if (possibleInspectors.some((id) => Number(id) === Number(user.id))) {
    return true;
  }

  if (!status) return false;

  if (canUserSetCrmNextStepAssignee(ticket, user, state, workflowLocked, false)) {
    return true;
  }

  const awaiting = ticket.awaiting_user;
  if (Array.isArray(awaiting) && awaiting.some((u) => Number(u?.id) === Number(user.id))) {
    return true;
  }

  return false;
}

/** Po nastavení výsledku kontroly už nemožno meniť kontrolóra – parita FP checkInspector. */
export function isCrmInspectorReassignLocked(row) {
  return Number(row?.inspector_state ?? 0) > 0;
}

/**
 * Môže nastaviť stav výsledku kontroly – parita ApprovalsPanel::canSetState + FP checkInspectorState.
 * Len priradený kontrolór (bez admin override).
 */
export function canSetCrmInspectorState(ticket, user, row, crmStates = [], workflowLocked = false) {
  if (!ticket || !user || !row) return false;
  if (workflowLocked || ticket.cancelled_at) return false;
  if (!row.inspector_id) return false;

  const states = Array.isArray(crmStates) ? crmStates : [];
  const statusId = Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0);
  const status = states.find((s) => Number(s.id) === statusId);
  if (!status) return false;
  if (isCrmWorkflowFinalStatus(status)) return false;
  if (['created', 'system_approval'].includes(String(status.phase || ''))) return false;

  const inspectorStateId = Number(row.inspector_state ?? 0);
  if (inspectorStateId > 0) {
    const inspectorState = states.find((s) => Number(s.id) === inspectorStateId);
    const phase = String(inspectorState?.phase || '');
    const tt = String(inspectorState?.transition_type || '');
    if (phase === 'inspection' && (tt === 'section_final' || tt === 'final_auto')) return false;
    if (tt === 'final_auto' || tt === 'final_manually') return false;
  }

  return Number(row.inspector_id) === Number(user.id);
}

export { isCrmApprovalInProgress };
